import React, { useEffect } from 'react';
import LocationList from '../../LocationList';
import '../../styles/modes/autoMode.css';

// Import components
import ModeHelpTooltip from './components/ModeHelpTooltip';
import StepVisualizer from './components/StepVisualizer';
import AlgorithmDetails from './components/AlgorithmDetails';
import SolveResult from './components/SolveResult';

// Import hooks
import { useAlgorithms } from './hooks/useAlgorithms';
import { usePathSolver } from './hooks/usePathSolver';
import { usePathDetails } from './hooks/usePathDetails';
import { useHelp } from './hooks/useHelp';

// Import context
import { useTsp } from '../../../context/TspContext';

/**
 * Auto Mode component for TSP
 * 
 * @returns {JSX.Element}
 */
export default function AutoMode() {
  const { 
    locations, 
    startId, 
    setLocations, 
    setStartId, 
    setPath, 
    autoPath: path,
    currentModeRef,
    handleUpdateVisiblePath: onUpdateVisiblePath
  } = useTsp();
  
  // Component state from custom hooks
  const {
    selectedAlgorithm,
    algorithms,
    isLoadingAlgorithms,
    handleAlgorithmChange,
    getAlgorithmOptions,
    algorithmInfo
  } = useAlgorithms();

  const {
    isSolving,
    solveError,
    solveResult,
    solvePath
  } = usePathSolver({
    locations,
    startId,
    selectedAlgorithm,
    setPath,
    currentModeRef,
    path
  });

  const {
    showPathDetails,
    currentStepIndex,
    togglePathDetails,
    handleStepChange
  } = usePathDetails({
    solveResult,
    onUpdateVisiblePath
  });

  const {
    showHelp,
    toggleHelp
  } = useHelp();
  
  // Log props for debugging
  useEffect(() => {
    console.log("[AUTO-MODE-DEBUG] Component props:", { locations, startId, path });
  }, [locations, startId, path, onUpdateVisiblePath]);
  
  return (
    <div className="auto-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Auto Find Path</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Auto Path mode"
          >
            ?
          </button>
        </div>
        <p>Use algorithms to automatically find optimal or near-optimal paths.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={toggleHelp} />}
      
      {/* Display location list, allow editing type and start point, but not deletion */}
      <LocationList 
        locations={locations}
        startId={startId}
        mode="Auto Find Path"
        readOnly={false}
        showActions={true}
        allowDelete={false}
        allowSetStart={true}
        onLocationChange={setLocations}
        onStartIdChange={setStartId}
      />
      
      {locations.length > 0 ? (
        <div className="auto-path-solver">
          {/* Display solving result */}
          <SolveResult
            solveResult={solveResult}
            locations={locations}
            algorithms={algorithms}
            showPathDetails={showPathDetails}
            togglePathDetails={togglePathDetails}
          />
          
          {/* Path details section - step by step visualization */}
          {showPathDetails && solveResult && (
            <div className="path-details-section">
              <h5>Path Details</h5>
              <StepVisualizer 
                steps={solveResult}
                currentStepIndex={currentStepIndex}
                onStepChange={handleStepChange}
                locations={locations}
                onPathVisualizationChange={onUpdateVisiblePath}
              />
            </div>
          )}
          
          {/* Solve button and algorithm selector */}
          <div className="solver-actions">
            <button 
              className="solve-button"
              onClick={solvePath}
              disabled={locations.length < 2 || !startId || isSolving || isLoadingAlgorithms}
            >
              {isSolving ? 'Solving...' : 'Auto Solve'}
            </button>
            
            {/* Algorithm selector */}
            <div className="algorithm-selector">

              <select
                id="algorithm-select"
                className="algorithm-select"
                value={selectedAlgorithm}
                onChange={handleAlgorithmChange}
                disabled={isSolving || isLoadingAlgorithms}
              >
                {isLoadingAlgorithms ? (
                  <option value="loading">Loading...</option>
                ) : (
                  getAlgorithmOptions()
                )}
              </select>
            </div>
            
            {/* Start point hint */}
            {locations.length >= 2 && !startId && (
              <p className="start-point-hint">Please set a start point before solving</p>
            )}
          </div>
          
          {/* Display error message */}
          {solveError && (
            <div className="solve-error">
              <p>{solveError}</p>
            </div>
          )}
          
          {/* Algorithm details panel */}
          <AlgorithmDetails 
            algorithmInfo={algorithmInfo}
            isLoading={isLoadingAlgorithms}
          />
        </div>
      ) : null}
    </div>
  );
} 